import UrgentRequestForm, { FormType, UrgentRequestFormData } from '../../components/orders/urgent-request-form';
import OrderSearchForm from '../../components/orders/order-search-form';
import DeliveryDateHistoryModal from '../../components/orders/delivery-date-history-modal';
import MessageHelper from '../../helpers/message-helper';
import AbstractAsyncTablePage from '../abstract-async-table-page';
import Table from '../../components/shared/table';
import HTMLTemplateHelper from '../../helpers/html-template-helper';
import SalesOrderData from '../../model/sales-order-data';
import SalesOrderEntryData from '../../model/sales-order-entry-data';
import { formatDate } from '../../helpers/utils';
import { BatchFetcher } from '../../helpers/batch-fetcher';
import StockAndPriceData from '../../model/stock-and-price-data';
import DeliveryAvailability from '../../components/product/delivery-availability';

interface SalesOrderEntryDataExtended extends SalesOrderEntryData {
  orderItemId: string;
  itemId: number;
  orderId: string;
  externalId: string;
  voucherDate: number;
}

interface StockAndPriceDataExtended extends StockAndPriceData {
  materialNumber: string;
  eligibleForMasterClassData: boolean;
  estimatedDeliveryDate: number;
  estimatedDeliveryDateFormatted: string;
  orderQuantity: number;
  openedQuantity: number;
}

export default class BackOrderPage extends AbstractAsyncTablePage {
  protected readonly searchForm: OrderSearchForm;
  protected readonly table: Table;
  protected readonly tableColumnDefinitions: DataTables.ColumnDefsSettings[] = [{
    targets: 0,
    data: 'orderId',
  }, {
    targets: 1,
    data: 'externalId',
  }, {
    targets: 2,
    data: (data: SalesOrderEntryDataExtended, type: string): string | number => {
      if (data.voucherDate === null) {
        return 0;
      }
      if (type === 'sort') {
        return data.voucherDate;
      }
      return formatDate(new Date(data.voucherDate));
    },
  }, {
    targets: 3,
    data: 'materialNumber',
  }, {
    targets: 4,
    data: 'description',
  }, {
    targets: 5,
    data: 'reference',
  }, {
    targets: 6,
    data: 'orderQuantity',
  }, {
    targets: 7,
    data: 'deliveredQuantity',
  }, {
    targets: 8,
    className: 'backorder-availability-data-cell',
    orderable: false,
    data: (data: SalesOrderEntryDataExtended): StockAndPriceDataExtended | SalesOrderEntryDataExtended | null => {
      if (data.eligibleForMasterClassData) {
        const stockData = this.asyncStockData[`${data.orderItemId}`];
        if (!stockData) {
          return null;
        }
        return {
          ...data,
          ...stockData,
        };
      }
      return data;
    },
    render: (data: StockAndPriceDataExtended | null): string => {
      if (!data) {
        return '<span class="loading-indicator"></span>';
      }
      if (data.eligibleForMasterClassData) {
        return `<div
          data-code="${data.materialNumber}"
          data-estimated-delivery-date-formatted="${data.estimatedDeliveryDateFormatted}"
          data-render-type="backorder"
          data-variant="no-icon"
          class="delivery-dates"
        ></div>`;
      }
      return data.estimatedDeliveryDateFormatted;
    },
    createdCell: (cell: Node, cellData: StockAndPriceDataExtended): void => {
      if (cellData?.eligibleForMasterClassData) {
        const container = (cell as HTMLElement).children[0] as HTMLDivElement;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const instance: DeliveryAvailability = new DeliveryAvailability(container, cellData.stockForecasts);
      }
    },
  }, {
    targets: 9,
    className: 'text-align-center',
    data: (data: SalesOrderEntryDataExtended): string | null => {
      if (!data.urgencyRequest) {
        return null;
      }

      let icon = 'envelope';
      let color: string;
      switch (data.urgencyRequest.urgencyRequest) {
        case 'REQUEST_ACCESSORIES':
          color = 'yellow';
          break;
        case 'REQUEST_ORIGINAL_PARTS':
          color = 'red';
          break;
        case 'READ_REQUEST_PARTS':
        case 'READ_REQUEST_ACCESSORIES':
          icon = 'envelope-open-text';
          color = 'green';
          break;
        default:
          color = '';
      }
      if (!color.length) {
        return null;
      }

      return HTMLTemplateHelper.createButton({
        text: MessageHelper.getMessage('common.open'),
        textClass: 'sr-only',
        icon: `fas fa-${icon}`,
        buttonClass: `-clear -compact js-openUrgencyRequest color-${color}`,
        dataAttributes: { id: data.orderItemId.toString() },
      }).outerHTML;
    },
  }];

  private rows: SalesOrderEntryDataExtended[] = [];
  private asyncStockData: Record<string, StockAndPriceData> = {};

  constructor(private body: JQuery) {
    super();
    this.searchForm = new OrderSearchForm(body.find('.js-backorderForm') as JQuery<HTMLFormElement>);
    this.table = new Table(body.find('.js-table'), this.tableColumnDefinitions);

    this.bindOpenUrgencyRequestModal();
    this.bindOpenDeliveryDateHistoryModal();
    this.bindAsyncData();
  }

  private bindOpenUrgencyRequestModal(): void {
    this.body.on('click', '.js-openUrgencyRequest', (e) => {
      const button = e.target as HTMLButtonElement;
      const rowData = this.rows.find((row) => row.orderItemId === button.dataset.id);

      if (!rowData) return;

      const deliveryTicketId = rowData.deliveryTicketData?.id;
      if (deliveryTicketId) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        $.get(`${window.ACC.config.encodedContextPath}/orders/backorder/urgencyRequest/${deliveryTicketId}`)
          .fail((error) => window.NotificationHelper.showError(error.responseJSON))
          .done((response: UrgentRequestFormData) => {
            BackOrderPage.openUrgentRequestModal(rowData, response);
          });
      } else {
        BackOrderPage.openUrgentRequestModal(rowData);
      }
    });
  }

  private bindOpenDeliveryDateHistoryModal(): void {
    this.body.on('click', '.js-openDeliveryDateHistory', (e) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const dates = this.rows.find((row) => row.id === Number(e.target.dataset.id))?.deliveryDateChangeHistory;
        if (dates) {
          const deliveryDateModal = new DeliveryDateHistoryModal(dates);
          deliveryDateModal.open();
        }
      } catch (error) {
        window.NotificationHelper.showError(MessageHelper.getMessage('eparts.orderfeatures.backorder.deliveryhistory.error'));
      }
    });
  }

  static openUrgentRequestModal(rowData: SalesOrderEntryDataExtended, requestFormData?: UrgentRequestFormData): void {
    const {
      itemId,
      orderId,
      materialNumber,
      description,
      orderQuantity,
      equipment,
    } = rowData;

    const formData = {
      itemId,
      orderId,
      materialNumber,
      description,
      equipment,
      quantity: orderQuantity,
      salesTicketId: rowData.deliveryTicketData?.id,
      formType: rowData.urgencyRequest?.urgencyRequest as FormType,
    };

    if (requestFormData) {
      Object.assign(formData, requestFormData);
      formData.materialNumber = rowData.materialNumber;
    }

    const urgentRequestForm = new UrgentRequestForm(formData);
    urgentRequestForm.open();
  }

  protected setRows(rows: SalesOrderData[]): void {
    const mappedData: SalesOrderEntryDataExtended[] = rows.flatMap(
      (data: SalesOrderData) => data.entries.map((entry: SalesOrderEntryData) => ({
        orderItemId: `${data.orderId}_${entry.id}`,
        itemId: entry.id,
        orderId: data.orderId,
        externalId: data.externalId,
        voucherDate: data.voucherDate,
        ...entry,
      })),
    );
    this.rows = mappedData;
    this.table.setRows(mappedData);

    const stockRequestData = mappedData
      .filter((entry) => entry.eligibleForMasterClassData)
      .map((entry) => ({
        productCode: entry.materialNumber,
        quantity: entry.orderQuantity,
        orderNumber: entry.orderId,
        orderPosition: entry.itemId,
      }));

    const endpoint = `${window.ACC.config.encodedContextPath}/products/masterStock`;
    const fetcher = new BatchFetcher<typeof stockRequestData[number], StockAndPriceData>(() => endpoint);

    void fetcher.fetchChunked(
      stockRequestData,
      (response) => {
        this.asyncStockData = response.reduce<Record<string, StockAndPriceData>>((map, data) => {
          // eslint-disable-next-line no-param-reassign
          map[`${String(data.orderNumber)}_${String(data.orderPosition)}`] = data;
          return map;
        }, this.asyncStockData);

        // Clear and re-add rows to get them to render properly
        this.table.getInstance()
          .clear()
          .rows.add(this.rows)
          .draw();
      },
    );
  }
}
